import { useState, useCallback, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { ThemeContext, type Theme } from './useThemeContext';

// Client-hydration flag without setState-in-an-effect. `useSyncExternalStore`
// returns the server snapshot (`false`) for the initial hydration render, then
// the client snapshot (`true`) once hydrated — yielding the same false→true
// transition the previous mounted state provided. The store never changes, so
// no subscription work is needed.
const noopSubscribe = () => () => {};
function useMounted(): boolean {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme to use if no preference is stored */
  defaultTheme?: Theme;
}

function getSystemTheme(): Theme {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function getStoredTheme(): Theme | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('theme') as Theme | null;
  }
  return null;
}

function setStoredTheme(theme: Theme): void {
  localStorage.setItem('theme', theme);
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Get stored theme, or use default, or fall back to system preference
    return getStoredTheme() || defaultTheme || getSystemTheme();
  });
  const mounted = useMounted();

  // Apply the theme to the DOM on mount and whenever it changes. This is a
  // genuine external side-effect, so it stays in an effect.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't explicitly set a preference
      if (!getStoredTheme()) {
        const newTheme = e.matches ? 'dark' : 'light';
        setThemeState(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    setStoredTheme(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      setStoredTheme(newTheme);
      return newTheme;
    });
  }, []);

  const value = {
    theme,
    toggleTheme,
    setTheme,
    mounted,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
